import type { Map as MapboxMap, MapLayerMouseEvent } from "mapbox-gl";

import { useEffect, useMemo, useRef } from "react";

import { normalizeOverlays } from "./overlay-colors";
import {
	ensureOverlayLayers,
	MAP_OVERLAY_FILL_LAYER_ID,
	MAP_OVERLAY_LINE_LAYER_ID,
	MAP_OVERLAY_SOURCE_ID,
	removeOverlayLayers,
} from "./overlay-layers";

export { assignOverlayColors } from "./overlay-colors";
export {
	MAP_OVERLAY_FILL_LAYER_ID,
	MAP_OVERLAY_LINE_LAYER_ID,
} from "./overlay-layers";

/** Soften dimmed → hover so opacity changes don't pop. */
const OVERLAY_HOVER_MS = 150;

/** Delay single-click when dblclick-to-edit is enabled (avoids mid-gesture map fit). */
const DOUBLE_CLICK_GUARD_MS = 150;

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

/** Renders GeoJSON polygon/line overlays on a Mapbox map (re-applied after style changes). */
export const useMapOverlays = (
	map: MapboxMap | null,
	isMapLoaded: boolean,
	overlays?: GeoJSON.Feature[] | GeoJSON.FeatureCollection,
	isDarkTheme = false,
	options: {
		interactive?: boolean;
		onOverlayClick?: (feature: GeoJSON.Feature) => void;
		onOverlayContextMenu?: (
			feature: GeoJSON.Feature,
			point: { x: number; y: number },
		) => void;
		onOverlayDoubleClick?: (feature: GeoJSON.Feature) => void;
		/** Let every zone overlay click fall through, such as while editing a floor. */
		passthroughZoneClicks?: boolean;
		/**
		 * Zone id whose overlay clicks should fall through (e.g. the zone whose
		 * boundary is being edited with Mapbox Draw).
		 */
		passthroughZoneId?: string;
	} = {},
) => {
	const {
		interactive = false,
		onOverlayClick,
		onOverlayContextMenu,
		onOverlayDoubleClick,
		passthroughZoneClicks,
		passthroughZoneId,
	} = options;
	const onOverlayClickRef = useRef(onOverlayClick);
	const onOverlayContextMenuRef = useRef(onOverlayContextMenu);
	const onOverlayDoubleClickRef = useRef(onOverlayDoubleClick);
	const passthroughZoneIdRef = useRef(passthroughZoneId);
	const passthroughZoneClicksRef = useRef(passthroughZoneClicks);

	useEffect(() => {
		onOverlayClickRef.current = onOverlayClick;
		onOverlayContextMenuRef.current = onOverlayContextMenu;
		onOverlayDoubleClickRef.current = onOverlayDoubleClick;
		passthroughZoneClicksRef.current = passthroughZoneClicks;
		passthroughZoneIdRef.current = passthroughZoneId;
	}, [
		onOverlayClick,
		onOverlayContextMenu,
		onOverlayDoubleClick,
		passthroughZoneClicks,
		passthroughZoneId,
	]);

	const overlayCollection = useMemo(
		() => normalizeOverlays(overlays),
		[overlays],
	);

	useEffect(() => {
		if (!map) return;

		const syncOverlays = () => {
			if (overlayCollection.features.length === 0) {
				removeOverlayLayers(map);
				return;
			}

			ensureOverlayLayers(map, overlayCollection, isDarkTheme);
		};

		if (isMapLoaded) {
			syncOverlays();
		}

		map.on("style.load", syncOverlays);
		return () => {
			map.off("style.load", syncOverlays);
		};
	}, [isDarkTheme, isMapLoaded, map, overlayCollection]);

	useEffect(() => {
		if (!map || !isMapLoaded || !interactive) return;
		if (
			overlayCollection.features.length === 0 ||
			(!map.getLayer(MAP_OVERLAY_FILL_LAYER_ID) &&
				!map.getLayer(MAP_OVERLAY_LINE_LAYER_ID))
		) {
			return;
		}

		let pendingClickTimeoutId: null | number = null;

		const isPassthroughZone = (feature: GeoJSON.Feature) => {
			if (passthroughZoneClicksRef.current) return true;

			const zoneId =
				typeof feature.properties?.zoneId === "string"
					? feature.properties.zoneId
					: undefined;
			return Boolean(
				passthroughZoneIdRef.current &&
				zoneId &&
				zoneId === passthroughZoneIdRef.current,
			);
		};

		const isFloorOverlay = (feature: GeoJSON.Feature) =>
			feature.properties?.overlayKind === "floor";

		const handleClick = (event: MapLayerMouseEvent) => {
			const feature = event.features?.[0];
			if (!feature) return;
			if (isFloorOverlay(feature as GeoJSON.Feature)) return;
			if (isPassthroughZone(feature as GeoJSON.Feature)) return;

			event.originalEvent.preventDefault();

			// Defer single-click when double-click edit is enabled so the first
			// click of a double-click does not refit the map mid-gesture.
			if (!onOverlayDoubleClickRef.current) {
				onOverlayClickRef.current?.(feature as GeoJSON.Feature);
				return;
			}

			if (pendingClickTimeoutId !== null) {
				window.clearTimeout(pendingClickTimeoutId);
			}
			pendingClickTimeoutId = window.setTimeout(() => {
				pendingClickTimeoutId = null;
				onOverlayClickRef.current?.(feature as GeoJSON.Feature);
			}, DOUBLE_CLICK_GUARD_MS);
		};

		const handleDoubleClick = (event: MapLayerMouseEvent) => {
			const feature = event.features?.[0];
			if (!feature) return;
			if (isFloorOverlay(feature as GeoJSON.Feature)) return;
			if (isPassthroughZone(feature as GeoJSON.Feature)) return;

			event.preventDefault();
			event.originalEvent.preventDefault();
			if (pendingClickTimeoutId !== null) {
				window.clearTimeout(pendingClickTimeoutId);
				pendingClickTimeoutId = null;
			}
			onOverlayDoubleClickRef.current?.(feature as GeoJSON.Feature);
		};

		const handleContextMenu = (event: MapLayerMouseEvent) => {
			const feature = event.features?.[0];
			if (!feature) return;
			if (
				!isFloorOverlay(feature as GeoJSON.Feature) &&
				isPassthroughZone(feature as GeoJSON.Feature)
			) {
				return;
			}

			event.preventDefault();
			event.originalEvent.preventDefault();
			onOverlayContextMenuRef.current?.(feature as GeoJSON.Feature, {
				x: event.originalEvent.clientX,
				y: event.originalEvent.clientY,
			});
		};

		let hoveredZoneId: null | string = null;
		let hoverProgress = 0;
		let hoverRafId: null | number = null;
		let hoverAnimationFrom = 0;
		let hoverAnimationTo = 0;
		let hoverAnimationStartedAt = 0;

		const setHoverProgress = (zoneId: string, progress: number) => {
			if (!map.getSource(MAP_OVERLAY_SOURCE_ID)) return;
			map.setFeatureState(
				{ id: zoneId, source: MAP_OVERLAY_SOURCE_ID },
				{ hover: progress },
			);
		};

		const stopHoverAnimation = () => {
			if (hoverRafId == null) return;
			cancelAnimationFrame(hoverRafId);
			hoverRafId = null;
		};

		const animateHoverProgress = (zoneId: string, to: number) => {
			stopHoverAnimation();
			hoverAnimationFrom = hoverProgress;
			hoverAnimationTo = to;
			hoverAnimationStartedAt = performance.now();

			const tick = (now: number) => {
				const elapsed = now - hoverAnimationStartedAt;
				const t = Math.min(1, elapsed / OVERLAY_HOVER_MS);
				hoverProgress =
					hoverAnimationFrom +
					(hoverAnimationTo - hoverAnimationFrom) * easeOutCubic(t);
				setHoverProgress(zoneId, hoverProgress);

				if (t < 1) {
					hoverRafId = requestAnimationFrame(tick);
					return;
				}

				hoverRafId = null;
				hoverProgress = hoverAnimationTo;
				if (hoverProgress <= 0 && hoveredZoneId === zoneId) {
					hoveredZoneId = null;
				}
			};

			hoverRafId = requestAnimationFrame(tick);
		};

		const clearHover = () => {
			if (hoveredZoneId == null) return;
			const zoneId = hoveredZoneId;
			animateHoverProgress(zoneId, 0);
		};

		const handleMouseMove = (event: MapLayerMouseEvent) => {
			const feature = event.features?.[0];
			if (
				passthroughZoneClicksRef.current ||
				(feature && isFloorOverlay(feature as GeoJSON.Feature))
			) {
				map.getCanvas().style.cursor = "";
				clearHover();
				return;
			}

			map.getCanvas().style.cursor = "pointer";
			const zoneId =
				typeof feature?.properties?.zoneId === "string"
					? feature.properties.zoneId
					: undefined;
			if (!zoneId) return;

			if (zoneId === hoveredZoneId) {
				// Re-entering during fade-out: reverse toward full hover.
				if (hoverProgress < 1) {
					animateHoverProgress(zoneId, 1);
				}
				return;
			}

			if (hoveredZoneId != null && hoveredZoneId !== zoneId) {
				stopHoverAnimation();
				setHoverProgress(hoveredZoneId, 0);
				hoverProgress = 0;
			}

			hoveredZoneId = zoneId;
			animateHoverProgress(zoneId, 1);
		};

		const handleMouseLeave = (event: MapLayerMouseEvent) => {
			const overlayLayers = [
				MAP_OVERLAY_FILL_LAYER_ID,
				MAP_OVERLAY_LINE_LAYER_ID,
			].filter((layerId) => Boolean(map.getLayer(layerId)));
			const stillOnOverlay =
				overlayLayers.length > 0 &&
				map.queryRenderedFeatures(event.point, { layers: overlayLayers })
					.length > 0;
			if (stillOnOverlay) return;

			map.getCanvas().style.cursor = "";
			clearHover();
		};

		map.on("click", MAP_OVERLAY_FILL_LAYER_ID, handleClick);
		map.on("click", MAP_OVERLAY_LINE_LAYER_ID, handleClick);
		map.on("contextmenu", MAP_OVERLAY_FILL_LAYER_ID, handleContextMenu);
		map.on("contextmenu", MAP_OVERLAY_LINE_LAYER_ID, handleContextMenu);
		map.on("dblclick", MAP_OVERLAY_FILL_LAYER_ID, handleDoubleClick);
		map.on("dblclick", MAP_OVERLAY_LINE_LAYER_ID, handleDoubleClick);
		map.on("mousemove", MAP_OVERLAY_FILL_LAYER_ID, handleMouseMove);
		map.on("mousemove", MAP_OVERLAY_LINE_LAYER_ID, handleMouseMove);
		map.on("mouseleave", MAP_OVERLAY_FILL_LAYER_ID, handleMouseLeave);
		map.on("mouseleave", MAP_OVERLAY_LINE_LAYER_ID, handleMouseLeave);

		return () => {
			if (pendingClickTimeoutId !== null) {
				window.clearTimeout(pendingClickTimeoutId);
			}
			stopHoverAnimation();
			if (hoveredZoneId != null && map.getSource(MAP_OVERLAY_SOURCE_ID)) {
				map.setFeatureState(
					{ id: hoveredZoneId, source: MAP_OVERLAY_SOURCE_ID },
					{ hover: 0 },
				);
			}
			hoveredZoneId = null;
			map.off("click", MAP_OVERLAY_FILL_LAYER_ID, handleClick);
			map.off("click", MAP_OVERLAY_LINE_LAYER_ID, handleClick);
			map.off("contextmenu", MAP_OVERLAY_FILL_LAYER_ID, handleContextMenu);
			map.off("contextmenu", MAP_OVERLAY_LINE_LAYER_ID, handleContextMenu);
			map.off("dblclick", MAP_OVERLAY_FILL_LAYER_ID, handleDoubleClick);
			map.off("dblclick", MAP_OVERLAY_LINE_LAYER_ID, handleDoubleClick);
			map.off("mousemove", MAP_OVERLAY_FILL_LAYER_ID, handleMouseMove);
			map.off("mousemove", MAP_OVERLAY_LINE_LAYER_ID, handleMouseMove);
			map.off("mouseleave", MAP_OVERLAY_FILL_LAYER_ID, handleMouseLeave);
			map.off("mouseleave", MAP_OVERLAY_LINE_LAYER_ID, handleMouseLeave);
			map.getCanvas().style.cursor = "";
		};
	}, [interactive, isMapLoaded, map, overlayCollection.features.length]);
};
